import {
  CORPORATE_PROGRAMME,
  PROGRAMME_PROFILE_KEYS,
  WORK_PROFILE_KEYS as PROGRAMME_WORK_PROFILE_KEYS,
  YOUTH_PROFILE_KEYS,
  YOUTH_PROGRAMME,
} from './programmeOptions';

export const OPTIONAL_PROFILE_KEYS = [
  'contact_no',
  'country',
  'address',
  'city',
  'pincode',
  'province',
  'skills',
  'occupation',
  'industry',
  'education_level',
  'language',
  'marketing_preferences',
  'referral_source',
  'date_of_birth',
  'learner_category',
  'employment_status',
];

export const JOB_PROFILE_KEYS = PROGRAMME_WORK_PROFILE_KEYS;

export const INTEGER_PROFILE_KEYS = [
  'number_of_direct_reports',
  'total_years_of_experience',
  'years_in_current_role',
  'nqf_level',
];

export const LEARNER_CATEGORY_OPTIONS = [
  { id: 'Reskilling', name: 'Reskilling' },
];

export const EMPLOYMENT_STATUS_OPTIONS = [
  { id: 'Employed', name: 'Employed' },
  { id: 'Unemployed', name: 'Unemployed' },
];

export const ADDITIONAL_PROFILE_FIELDS = [
  { name: 'contact_no', label: 'Contact Number', maxLength: 50 },
  { name: 'country', label: 'Country', maxLength: 100 },
  { name: 'address', label: 'Address', maxLength: 200 },
  { name: 'city', label: 'City', maxLength: 100 },
  { name: 'pincode', label: 'Pincode', maxLength: 20 },
  { name: 'province', label: 'Province', maxLength: 100 },
  { name: 'skills', label: 'Skills', maxLength: 200 },
  { name: 'occupation', label: 'Occupation', maxLength: 100 },
  { name: 'industry', label: 'Industry', maxLength: 100 },
  { name: 'education_level', label: 'Education Level', maxLength: 100 },
  { name: 'language', label: 'Language', maxLength: 100 },
  { name: 'marketing_preferences', label: 'Marketing Preferences', maxLength: 100 },
  { name: 'referral_source', label: 'Referral Source', maxLength: 100 },
  { name: 'date_of_birth', label: 'Date of Birth', type: 'date' },
];

export const JOB_PROFILE_FIELDS = [
  { name: 'job_title', label: 'Job Title', maxLength: 150 },
  { name: 'job_level', label: 'Job Level', maxLength: 100 },
  { name: 'number_of_direct_reports', label: 'Number of Direct Reports', type: 'integer' },
  { name: 'total_years_of_experience', label: 'Total Years of Experience', type: 'integer' },
  { name: 'years_in_current_role', label: 'Years of Experience in Current Role', type: 'integer' },
  { name: 'goals_or_objectives', label: 'Goals/Objectives', multiline: true, fullWidth: true },
];

const PROFILE_MAX_LENGTH = ADDITIONAL_PROFILE_FIELDS.reduce((limits, field) => {
  if (field.maxLength) limits[field.name] = { max: field.maxLength, label: field.label };
  return limits;
}, {
  learner_category: { max: 50, label: 'Learner Category' },
  employment_status: { max: 50, label: 'Employment Status' },
});

JOB_PROFILE_FIELDS.forEach((field) => {
  if (field.maxLength) {
    PROFILE_MAX_LENGTH[field.name] = { max: field.maxLength, label: field.label };
  }
});

const INTEGER_LABELS = {
  number_of_direct_reports: 'Number of Direct Reports',
  total_years_of_experience: 'Total Years of Experience',
  years_in_current_role: 'Years of Experience in Current Role',
};

export function formatLocalIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isReskillingEmployed(category, status) {
  return String(category || '').trim().toLowerCase() === 'reskilling'
    && String(status || '').trim().toLowerCase() === 'employed';
}

export function emptyProfileFields() {
  return {
    contact_no: '',
    country: '',
    address: '',
    city: '',
    pincode: '',
    province: '',
    skills: '',
    occupation: '',
    industry: '',
    education_level: '',
    language: '',
    marketing_preferences: '',
    referral_source: '',
    date_of_birth: '',
    learner_category: '',
    employment_status: '',
    job_title: '',
    job_level: '',
    division: '',
    line_manager: '',
    number_of_direct_reports: '',
    total_years_of_experience: '',
    years_in_current_role: '',
    goals_or_objectives: '',
    highest_qualification: '',
    nqf_level: '',
    field_of_study: '',
    host_employer: '',
    workplace_mentor: '',
    profile_image: '',
  };
}

export function isProfileImageDataUri(value) {
  return /^data:image\/(jpeg|png);base64,[A-Za-z0-9+/]+={0,2}$/i.test(String(value || '').trim());
}

export function profileImageForPayload(value, { isEdit = false } = {}) {
  if (value == null || String(value).trim() === '') return '';
  const image = String(value).trim();
  if (isProfileImageDataUri(image)) return image;
  if (isEdit && /^https?:\/\//i.test(image)) return image;
  return '';
}

export function validateProfileImage(value, { isEdit = false } = {}) {
  if (value == null || String(value).trim() === '') return null;
  if (profileImageForPayload(value, { isEdit })) return null;
  return 'Profile photo must be a JPEG or PNG.';
}

function normalizeLearnerCategory(value) {
  if (value == null || String(value).trim() === '') return '';
  if (String(value).trim().toLowerCase() === 'reskilling') return 'Reskilling';
  return String(value);
}

function normalizeEmploymentStatus(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'employed') return 'Employed';
  if (normalized === 'unemployed') return 'Unemployed';
  return String(value);
}

function normalizeDateOfBirth(value) {
  if (value == null || value === '') return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : String(value);
}

export function profileFieldsFromUser(user) {
  const fields = emptyProfileFields();
  if (!user) return fields;
  Object.keys(fields).forEach((key) => {
    const value = user[key];
    if (value == null) {
      fields[key] = '';
      return;
    }
    fields[key] = INTEGER_PROFILE_KEYS.includes(key) ? String(value) : value;
  });
  fields.learner_category = normalizeLearnerCategory(fields.learner_category);
  fields.employment_status = normalizeEmploymentStatus(fields.employment_status);
  fields.date_of_birth = normalizeDateOfBirth(fields.date_of_birth);
  return fields;
}

function isBlank(value) {
  return value == null || (typeof value === 'string' && value.trim() === '');
}

function emptyToNull(value) {
  if (isBlank(value)) return null;
  return typeof value === 'string' ? value.trim() : value;
}

export function validateProfileFields(formState, options = {}) {
  if (!formState) return null;

  const imageError = validateProfileImage(formState.profile_image, options);
  if (imageError) return imageError;

  const dateOfBirth = formState.date_of_birth;
  if (!isBlank(dateOfBirth)) {
    const value = String(dateOfBirth).trim();
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return 'Date of birth must be YYYY-MM-DD.';
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    const isRealDate = parsed.getFullYear() === year
      && parsed.getMonth() === month - 1
      && parsed.getDate() === day;
    if (!isRealDate) return 'Date of birth must be YYYY-MM-DD.';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsed > today) return 'Date of birth cannot be in the future.';
  }

  const programmeType = options.programmeType || '';
  const configuredFields = Array.isArray(options.profileFields) ? options.profileFields : [];
  const keysToCheck = programmeType
    ? Object.keys(PROFILE_MAX_LENGTH).filter(key => !JOB_PROFILE_KEYS.includes(key))
    : isReskillingEmployed(formState.learner_category, formState.employment_status)
    ? Object.keys(PROFILE_MAX_LENGTH)
    : Object.keys(PROFILE_MAX_LENGTH).filter((key) => !JOB_PROFILE_KEYS.includes(key));

  configuredFields.forEach(field => {
    if (field?.name && field.maxLength && !keysToCheck.includes(field.name)) {
      keysToCheck.push(field.name);
      PROFILE_MAX_LENGTH[field.name] = { max: field.maxLength, label: field.label || field.name };
    }
  });

  for (let index = 0; index < keysToCheck.length; index += 1) {
    const key = keysToCheck[index];
    const limit = PROFILE_MAX_LENGTH[key];
    const value = formState[key];
    if (!isBlank(value) && String(value).trim().length > limit.max) {
      return `${limit.label} cannot be longer than ${limit.max} characters.`;
    }
  }

  if (programmeType === YOUTH_PROGRAMME) {
    const nqfValue = formState.nqf_level;
    if (!isBlank(nqfValue)) {
      const nqfField = configuredFields.find(field => field.name === 'nqf_level') || {};
      const min = nqfField.min ?? 1;
      const max = nqfField.max ?? 10;
      if (!/^\d+$/.test(String(nqfValue).trim()) || Number(nqfValue) < min || Number(nqfValue) > max) {
        return `NQF level must be a whole number from ${min} to ${max}.`;
      }
    }
    return null;
  }

  if (programmeType && programmeType !== CORPORATE_PROGRAMME) return null;

  if (!programmeType && !isReskillingEmployed(formState.learner_category, formState.employment_status)) {
    return null;
  }

  for (let index = 0; index < INTEGER_PROFILE_KEYS.length; index += 1) {
    const key = INTEGER_PROFILE_KEYS[index];
    const value = formState[key];
    if (isBlank(value)) continue;
    if (!/^\d+$/.test(String(value).trim())) {
      return `${INTEGER_LABELS[key]} must be a whole number of 0 or more.`;
    }
  }

  const totalYears = formState.total_years_of_experience;
  const currentRoleYears = formState.years_in_current_role;
  if (!isBlank(totalYears) && !isBlank(currentRoleYears)
    && /^\d+$/.test(String(totalYears).trim())
    && /^\d+$/.test(String(currentRoleYears).trim())
    && Number(currentRoleYears) > Number(totalYears)) {
    return 'Years in the current role cannot exceed total years of experience.';
  }

  return null;
}

export function toMultipleUserCreateBody(users, projectId, options = {}) {
  return (users || []).map((user) => {
    const payload = applyProfileToPayload({
      ...user,
      userName: user.email,
      customers: (user.customers || []).map((company) => (company && company.uuid) || company),
      is_active: true,
    }, user, options);
    if (projectId) payload.project_id = projectId;
    return payload;
  });
}

export function applyProfileToPayload(payload, formState = payload, options = {}) {
  const next = { ...payload };
  OPTIONAL_PROFILE_KEYS.forEach((key) => {
    next[key] = emptyToNull(formState[key]);
  });

  const programmeType = options.programmeType || '';
  if (programmeType) {
    delete next.learner_category;
    delete next.employment_status;
    PROGRAMME_PROFILE_KEYS.forEach(key => delete next[key]);
    const allowedKeys = programmeType === CORPORATE_PROGRAMME
      ? PROGRAMME_WORK_PROFILE_KEYS
      : programmeType === YOUTH_PROGRAMME ? YOUTH_PROFILE_KEYS : [];
    const configuredFields = Array.isArray(options.profileFields) ? options.profileFields : [];
    const activeKeys = configuredFields.length
      ? allowedKeys.filter(key => configuredFields.some(field => field.name === key))
      : allowedKeys;
    activeKeys.forEach(key => {
      const descriptor = configuredFields.find(field => field.name === key);
      const value = formState[key];
      if (descriptor?.type === 'integer' || INTEGER_PROFILE_KEYS.includes(key)) {
        next[key] = isBlank(value) ? null : Number(String(value).trim());
      } else {
        next[key] = emptyToNull(value);
      }
    });
    const profileImage = profileImageForPayload(formState.profile_image, options);
    if (profileImage) next.profile_image = profileImage;
    else delete next.profile_image;
    return next;
  }

  if (!isReskillingEmployed(formState.learner_category, formState.employment_status)) {
    JOB_PROFILE_KEYS.forEach((key) => {
      delete next[key];
    });
  } else {
    JOB_PROFILE_KEYS.forEach((key) => {
      if (!INTEGER_PROFILE_KEYS.includes(key)) {
        next[key] = emptyToNull(formState[key]);
        return;
      }
      const value = formState[key];
      next[key] = isBlank(value) ? null : Number(String(value).trim());
    });
  }

  const profileImage = profileImageForPayload(formState.profile_image, options);
  if (profileImage) next.profile_image = profileImage;
  else delete next.profile_image;
  return next;
}

function formatFieldErrors(value) {
  if (value == null || value === false) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map(formatFieldErrors).filter(Boolean).join(' ');
  }
  if (typeof value !== 'object') return '';
  return Object.keys(value)
    .filter((key) => key !== 'status' && key !== 'data')
    .map((key) => formatFieldErrors(value[key]))
    .filter(Boolean)
    .join(' ');
}

export function formatApiErrorMessage(result, fallback = 'Something Went Wrong') {
  if (result == null || result === '') return fallback;
  if (typeof result === 'string') return result;

  const failedUsers = result.data && Array.isArray(result.data.failed_users)
    ? result.data.failed_users
    : [];
  const failedText = failedUsers.map((user) => {
    const who = user.email || user.Username || user.username || 'User';
    const why = user.error || user.reason || '';
    return why ? `${who}: ${why}` : who;
  }).filter(Boolean).join('; ');

  let messageText = '';
  if (typeof result.message === 'string' && result.message.trim()) {
    messageText = result.message.trim();
  } else if (result.message && typeof result.message === 'object') {
    messageText = formatFieldErrors(result.message);
  } else {
    messageText = formatFieldErrors(result);
  }

  if (messageText && failedText) return `${messageText} ${failedText}`;
  return messageText || failedText || fallback;
}

export function formatBulkResultLines(data) {
  const failed = Array.isArray(data?.Failed) ? data.Failed : [];
  const processed = Array.isArray(data?.Processed)
    ? data.Processed
    : (Array.isArray(data?.Created) ? data.Created : []);
  return {
    failed: failed.map((row) => ({
      username: row?.Username || row?.username || 'User',
      reason: row?.reason || row?.Reason || '',
    })),
    processed: processed.map((row) => (typeof row === 'string'
      ? { username: row, action: '' }
      : {
        username: row?.Username || row?.username || row?.email || 'User',
        action: row?.Action || row?.action || '',
      })),
  };
}
